import { useMemo } from 'react';
import styled from '@emotion/styled';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { type Chat, type ChatResponseType } from '@trinity/chat';
import { Table } from '@trinity/chat-components/components/Chat.Attachments/Table';
import { TextQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint/index.js';
import { useRepSelectionAction } from './hooks/useRepSelectionAction';
import { BasicSystemUserMessage } from './Message.System.Basic';
import { MessageContainer } from './Message.System.Wrapper';

type Props = {
  queryIdentifier: string;
  response: Chat.QueryResponse<typeof ChatResponseType.RepSearchResults>;
  disabled?: boolean;
};

export const RepSearchResultsResponse = ({ response, disabled = false }: Props) => {
  const submitRepSelection = useRepSelectionAction();
  const results = response.data.results;

  const columns = useMemo<GridColDef[]>(() => {
    return [
      { field: 'repId', headerName: 'Rep Id', type: 'string', minWidth: 80, maxWidth: 220 },
      {
        field: 'profile',
        headerName: 'Profile',
        type: 'string',
        minWidth: 90,
        maxWidth: 90,
        sortable: false,
        renderCell: (params: GridRenderCellParams<Record<string, unknown>>) => {
          const repId = params.row.repId;
          const repName = params.row.name;

          if (typeof repId !== 'string' || typeof repName !== 'string') {
            return null;
          }

          return (
            <StyledViewProfileButton
              disabled={disabled}
              onClick={() => {
                if (disabled) {
                  return;
                }

                submitRepSelection({ id: repId, name: repName });
              }}
              value="View"
            />
          );
        },
      },
      { field: 'name', headerName: 'Name', type: 'string', minWidth: 120, maxWidth: 300 },
      { field: 'district', headerName: 'District', type: 'string', minWidth: 140, maxWidth: 220 },
      { field: 'region', headerName: 'Region', type: 'string', minWidth: 140, maxWidth: 220 },
      { field: 'territory', headerName: 'Territory', type: 'string', minWidth: 160, maxWidth: 260 },
    ];
  }, [disabled, submitRepSelection]);

  const rows = useMemo<Array<Record<string, unknown>>>(() => {
    return results.map((rep) => ({
      repId: rep.id,
      profile: 'View',
      name: rep.name,
      district: rep.district,
      region: rep.region,
      territory: rep.territory,
    }));
  }, [results]);

  return (
    <MessageContainer>
      <BasicSystemUserMessage>
        <Body>
          <Message>
            Sure, I found the following matching sales representatives. Select a rep by clicking
            &quot;View&quot; and ask additional questions.
          </Message>

          {results.length > 0 ? (
            <Table
              tableId={`rep-search-results-${response.data.search.name}-${results.length}`}
              columns={columns}
              rows={rows}
              title="rep-search-results"
              showExportCsv={false}
              pinnedLeft={['repId', 'profile', 'name']}
            />
          ) : (
            <NoResults>No reps were found for your search criteria.</NoResults>
          )}
        </Body>
      </BasicSystemUserMessage>
    </MessageContainer>
  );
};

const Body = styled.div({
  width: '100%',
});

const Message = styled.div({
  marginBottom: 15,
});

const StyledViewProfileButton = styled(TextQueryHint)({
  minWidth: 65,
  maxWidth: 65,
});

const NoResults = styled.div(({ theme }) => ({
  marginTop: 10,
  fontFamily: theme.fonts.regular,
}));
